import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Fold } from "../components/Fold";
import { FareHistory } from "../components/FareHistory";
import { FareSparkline } from "../components/FareSparkline";
import { PageTitle, SectionTitle } from "../components/Heading";
import { Icon } from "../components/Icons";
import { Sheet } from "../components/Sheet";
import { useBusyRows } from "../hooks/useBusyRows";
import {
  activeFares,
  isStale,
  isWatched,
  localDay,
  markFor,
  money,
  neverPriced,
  newestReading,
  notWatching as isNotWatching,
  paidTotals,
  parseAmount,
  serpEstimate,
  shownQuotes,
  trackAt,
  trackCurrent,
  trackingParts,
  verdict,
  type CashOptions,
  type Options,
  type PriceSeries,
  type PriceDrop,
  type Quote,
  type Verdict,
  type WatchOptions,
} from "../lib/flightPrices";
import {
  AWARD_PROGRAMS,
  BOOKING_CHANNELS,
  CABINS,
  FIELDS,
  FLIGHT_AWARD_PROGRAMS,
  FORMS,
  flown,
  archiveTrip,
  archiveWatch,
  badge,
  changedFields,
  createTrip,
  createWatch,
  fetchFlights,
  fieldsFromForm,
  findDuplicate,
  formOf,
  formValues,
  lockedIn,
  underway,
  mergeTrip,
  missingRequired,
  needsYou,
  resolveMatch,
  dismissMatch,
  reviewPrice,
  runFlightJob,
  routeLabel,
  shortDate,
  tripGroups,
  updateTrip,
  updateWatch,
  watchEnd,
  whenLabel,
  withoutBlanks,
  type FieldSpec,
  type FlightsDto,
  type FlightJob,
  type Form,
  type FormValues,
  type Group,
  type Trip,
  type Watch,
} from "../lib/flightsPage";
import {
  NO_FILTER,
  filterCounts,
  filterGroups,
  hidesAnything,
  isFiltered,
  readTravelFilter,
  writeTravelFilter,
  type BookedFilter,
  type KindFilter,
  type TravelFilter,
  type TripFilter,
  type TravelerFilter,
  travelerOptions,
  travelerMatches,
  DEFAULT_FILTER,
  forTraveler,
  groupsForTraveler,
} from "../lib/travelFilters";

/**
 * Travel, manage mode (plan step 1c, 2026-09-12): the trips and watches
 * site-checker prices, what needs you, and the forms that add and edit them.
 * Configured baselines and observed prices share the same rows.
 *
 * Nothing here deletes. Archive keeps the row and site-checker's price
 * history for it, and an archived watch whose dates are still ahead can be
 * restored from the Archived list. Stop watching is separate: it only stops
 * the price checks.
 */
export default function Flights() {
  const [data, setData] = useState<FlightsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState<
    | { mode: "add" }
    | { mode: "edit"; watch: Watch }
    | { mode: "track"; fare: PriceDrop }
    | { mode: "paid" }
    | { mode: "trip"; trip: Trip }
    | { mode: "merge"; trip: Trip }
    | null
  >(null);
  const [filter, setFilterState] = useState<TravelFilter>(readTravelFilter);
  const setFilter = (next: TravelFilter) => {
    setFilterState(next);
    writeTravelFilter(next);
  };
  const [jobNotice, setJobNotice] = useState<string | null>(null);
  const { isBusy, run } = useBusyRows<string>();

  const load = useCallback(() => {
    fetchFlights()
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: unknown) => setError(message(e)));
  }, []);

  useEffect(load, [load]);

  useEffect(() => {
    if (!data) return;
    const groups = tripGroups(data, new Date());
    // A trip deleted, or a tag cleared or respelled, would otherwise leave the
    // page filtered to something no trip matches and looking empty for good.
    const tripGone = typeof filter.trip === "number"
      && !groups.some((group) => group.trip.id === filter.trip);
    // "all" and "mine" are always satisfiable; only a named person can vanish.
    const travelerGone = filter.traveler !== "all" && filter.traveler !== "mine"
      && !groups.some((group) => travelerMatches(group.trip.traveler, filter.traveler));
    if (!tripGone && !travelerGone) return;
    const next = {
      ...filter,
      ...(tripGone ? { trip: "all" as const } : {}),
      ...(travelerGone ? { traveler: DEFAULT_FILTER.traveler } : {}),
    };
    setFilterState(next);
    writeTravelFilter(next);
  }, [data, filter]);

  const act = (key: string, work: Promise<unknown>) =>
    void run(key, work.then(load).catch((e: unknown) => setError(message(e))));

  if (error && !data) {
    return <div className="rounded-md border border-red-900 bg-red-950/40 p-4 text-sm">{error}</div>;
  }
  if (!data) return <div className="text-sm text-neutral-400">Loading…</div>;

  const now = new Date();
  const live = data.watches.filter((w) => !w.archived && !underway(w, now));
  const groups = tripGroups(data, now);
  const needs = needsYou(data);
  const prices = data.prices ?? {};
  const shown = filterGroups(groups, filter, prices);
  const shownCount = shown.reduce((n, g) => n + g.watches.length, 0);
  const narrowed = hidesAnything(groups, shown);
  const newest = newestReading(data);
  const today = localDay();
  const drops = activeFares(data, today);
  // The headline follows the traveler choice only: it answers "what am I
  // tracking for this person", so it must not contradict the list under it,
  // and must not double as the filter bar's own "Showing N of M".
  const tripList = groups.map((g) => g.trip);
  const mine = forTraveler(live, tripList, filter.traveler);
  const mineTrips = groupsForTraveler(groups, filter.traveler).length;
  const booked = mine.filter((w) => w.fields.booked).length;
  const done = () => {
    setSheet(null);
    load();
  };
  const startJob = (job: FlightJob, question: string) => {
    if (!window.confirm(question)) return;
    setJobNotice(null);
    void run(
      `job:${job}`,
      runFlightJob(job)
        .then((started) => setJobNotice(`${started.label} started.`))
        .catch((e: unknown) => setJobNotice(message(e))),
    );
  };

  return (
    <section className="space-y-6">
      <PageTitle>Travel</PageTitle>

      {error && (
        <div className="rounded-md border border-red-900 bg-red-950/40 p-3 text-sm">{error}</div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="hero-num text-[42px]">{mine.length}</span>
          <span className="text-sm text-neutral-400">
            {mine.length === 1 ? "watch" : "watches"} across {mineTrips}{" "}
            {mineTrips === 1 ? "trip" : "trips"} · {booked} booked,{" "}
            {mine.length - booked} still looking
          </span>
        </div>
        <span className="grow" />
        <button type="button" onClick={() => setSheet({ mode: "add" })} className="btn btn-primary">
          + Track a flight
        </button>
      </div>

      <PriceDrops
        drops={drops}
        busy={(fare) => isBusy(dropKey(fare))}
        onTrack={(fare) => setSheet({ mode: "track", fare })}
        onKeep={(fare) => act(dropKey(fare), markReviewed(fare))}
      />

      <TrackerTools
        estimate={serpEstimate(live, today)}
        notice={jobNotice}
        busy={(job) => isBusy(`job:${job}`)}
        onRun={startJob}
      />

      <NeedsYou
        data={data}
        needs={needs}
        isBusy={isBusy}
        act={act}
        onAddPaid={() => setSheet({ mode: "paid" })}
      />

      {live.length === 0 && (
        <div className="rounded-2xl border border-dashed border-neutral-700 p-5 text-sm">
          <p className="font-semibold">Nothing tracked yet.</p>
          <p className="mt-1 text-neutral-400">
            Track a flight, a trip or a hotel, and site-checker prices it every morning.
          </p>
        </div>
      )}

      {live.length > 0 && (
        <TravelFilters
          filter={filter}
          counts={filterCounts(mine, filter, prices)}
          trips={tripList}
          shown={shownCount}
          total={live.length}
          narrowed={narrowed}
          onChange={setFilter}
        />
      )}

      {live.length > 0 && isFiltered(filter) && shown.length === 0 && (
        <div className="rounded-2xl border border-dashed border-neutral-700 p-5 text-sm text-neutral-400">
          Nothing matches these filters.
        </div>
      )}

      {shown.map((g) => (
        <TripSection
          key={g.trip.id}
          group={g}
          totals={paidTotals(
            groups.find((candidate) => candidate.trip.id === g.trip.id)?.watches ?? g.watches,
          )}
          onEditTrip={() => setSheet({ mode: "trip", trip: g.trip })}
          busy={isBusy(`t:${g.trip.id}`)}
          isBusy={isBusy}
          onEdit={(watch) => setSheet({ mode: "edit", watch })}
          prices={prices}
          options={data.options ?? {}}
          newest={newest}
        />
      ))}

      <ArchivedWatches
        watches={data.watches.filter((w) => w.archived && w.kind !== "query"
          && w.fields.booked && !ended(w, now, today))}
        trips={data.trips}
        isBusy={isBusy}
        onRestore={(w) => {
          // A live watch in an archived trip is refused by merge and missing
          // from the trip picker, so its trip comes back with it.
          const trip = data.trips.find((t) => t.id === w.trip_id);
          act(`w:${w.watch_id}`, updateWatch(w.watch_id, { archived: false }).then(() =>
            trip?.archived ? updateTrip(trip.id, { archived: false }) : undefined));
        }}
      />

      {sheet?.mode === "add" && (
        <WatchSheet data={data} onClose={() => setSheet(null)} onDone={done} />
      )}
      {sheet?.mode === "edit" && (
        <WatchSheet
          data={data}
          watch={sheet.watch}
          onClose={() => setSheet(null)}
          onDone={done}
        />
      )}
      {sheet?.mode === "track" && (
        <TrackSheet fare={sheet.fare} onClose={() => setSheet(null)} onDone={done} />
      )}
      {sheet?.mode === "paid" && (
        <PaidSheet watches={needs.unpaid} onClose={() => setSheet(null)} onDone={done} />
      )}
      {sheet?.mode === "merge" && (
        <MergeTripSheet
          source={sheet.trip}
          trips={data.trips}
          watches={data.watches}
          onClose={() => setSheet(null)}
          onDone={done}
        />
      )}
      {sheet?.mode === "trip" && (
        <TripEditSheet
          trip={sheet.trip}
          canMerge={data.trips.some(
            (candidate) => candidate.id !== sheet.trip.id && !candidate.archived && !candidate.merged_into_id,
          )}
          onMerge={() => setSheet({ mode: "merge", trip: sheet.trip })}
          onClose={() => setSheet(null)}
          onDone={done}
        />
      )}
    </section>
  );
}

const BOOKED_CHOICES: [BookedFilter, string][] = [
  ["all", "All"],
  ["booked", "Booked"],
  ["open", "Not booked"],
];

const KIND_CHOICES: [KindFilter, string][] = [
  ["all", "Everything"],
  ["flight", "Flights"],
  ["hotel", "Hotels"],
];

const PILL = "min-h-9 shrink-0 rounded-full border px-3.5 text-[13px] font-semibold";
const PILL_ON = "border-accent bg-accent text-neutral-950";
const PILL_OFF = "border-neutral-700 text-neutral-200 hover:border-neutral-500";

/** Narrows the trip list. Sticks under the header so it stays in reach while
 * scrolling a long list on a phone; the chip row scrolls sideways there. */
function TravelFilters({
  filter,
  counts,
  trips,
  shown,
  total,
  narrowed,
  onChange,
}: {
  filter: TravelFilter;
  counts: ReturnType<typeof filterCounts>;
  trips: Trip[];
  shown: number;
  total: number;
  /** Whether any trip or watch is hidden, which watch counts alone can miss. */
  narrowed: boolean;
  onChange: (next: TravelFilter) => void;
}) {
  const set = (over: Partial<TravelFilter>) => onChange({ ...filter, ...over });
  const tripValue = filter.trip === "all" ? "all" : `trip:${filter.trip}`;
  const chooseTrip = (value: string): TripFilter => {
    if (value === "all") return "all";
    return Number(value.slice("trip:".length));
  };
  const people = travelerOptions(trips);
  return (
    <section
      aria-label="Filters"
      className="sticky top-[calc(57px+env(safe-area-inset-top))] z-20 -mx-4 space-y-2.5 border-y border-neutral-800/70 bg-neutral-950 px-4 py-2.5 sm:mx-0 sm:flex sm:flex-wrap sm:items-center sm:gap-x-4 sm:space-y-0 sm:px-0"
    >
      <div role="group" aria-label="Booked" className="flex rounded-full border border-neutral-700 p-[3px] sm:inline-flex">
        {BOOKED_CHOICES.map(([key, label]) => (
          <button
            key={key}
            type="button"
            aria-pressed={filter.booked === key}
            onClick={() => set({ booked: key })}
            className={`min-h-9 grow rounded-full px-3.5 text-[13px] font-semibold ${
              filter.booked === key ? "bg-accent text-neutral-950" : "text-neutral-200"
            }`}
          >
            {label} <span className="font-normal opacity-70">{counts.booked[key]}</span>
          </button>
        ))}
      </div>
      <div className="-mr-4 flex gap-2 overflow-x-auto pr-4 sm:mr-0 sm:flex-wrap sm:overflow-visible sm:pr-0">
        <select
          aria-label="Trip"
          value={tripValue}
          onChange={(event) => set({ trip: chooseTrip(event.target.value) })}
          className={`${PILL} max-w-[75vw] ${filter.trip !== "all" ? PILL_ON : `bg-neutral-950 ${PILL_OFF}`}`}
        >
          <option value="all">All trips</option>
          {trips.map((trip) => <option key={trip.id} value={`trip:${trip.id}`}>{trip.name}</option>)}
        </select>
        {people.length > 0 && (
          <select
            aria-label="Traveler"
            value={filter.traveler}
            onChange={(event) => set({ traveler: event.target.value as TravelerFilter })}
            className={`${PILL} max-w-[75vw] ${filter.traveler !== "all" ? PILL_ON : `bg-neutral-950 ${PILL_OFF}`}`}
          >
            <option value="all">Anyone</option>
            <option value="mine">Mine</option>
            {people.map((person) => (
              <option key={person.value} value={person.value}>{person.label}</option>
            ))}
          </select>
        )}
        <button
          type="button"
          aria-pressed={filter.below}
          onClick={() => set({ below: !filter.below })}
          className={`${PILL} ${
            filter.below ? "border-green-400 bg-green-400/15 text-green-400" : PILL_OFF
          }`}
        >
          Below tracked price <span className="font-normal opacity-70">{counts.below}</span>
        </button>
        <div role="group" aria-label="Kind" className="contents">
          {KIND_CHOICES.map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={filter.kind === key}
              onClick={() => set({ kind: key })}
              className={`${PILL} ${filter.kind === key ? PILL_ON : PILL_OFF}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {/* Only when something is actually hidden. The page opens on his own
          trips, so a filter that hides nothing is the resting state and does
          not need announcing or a button to undo. */}
      {isFiltered(filter) && narrowed && (
        <div className="flex items-center gap-3 text-xs text-neutral-400 sm:ml-auto">
          <span role="status">Showing {shown} of {total}</span>
          <button
            type="button"
            onClick={() => onChange(NO_FILTER)}
            className="min-h-9 font-semibold text-accent"
          >
            Clear filters
          </button>
        </div>
      )}
    </section>
  );
}

const dropKey = (fare: PriceDrop) => `drop:${fare.watch.watch_id}:${fare.mark.axis}`;

/** Hides this exact reading from Price drops; a later changed price shows again. */
const markReviewed = (fare: PriceDrop) =>
  reviewPrice(fare.watch.watch_id, fare.mark.axis, fare.quote.history[fare.quote.history.length - 1].at);

/**
 * Each drop asks one question: what should this watch be measured against now?
 * A new amount, confirmed in the Track dialog, or the one it
 * already has. Keeping it hides this reading only; a later reading still under
 * the baseline shows again.
 */
function PriceDrops({
  drops,
  busy,
  onTrack,
  onKeep,
}: {
  drops: PriceDrop[];
  busy: (fare: PriceDrop) => boolean;
  onTrack: (fare: PriceDrop) => void;
  onKeep: (fare: PriceDrop) => void;
}) {
  if (drops.length === 0) return null;
  return (
    <section aria-label="Price drops">
      <SectionTitle>Price drops · {drops.length}</SectionTitle>
      <div className="mt-2 grid border-y border-neutral-700/80 md:grid-cols-3">
        {drops.map((fare) => {
          const result = verdict(fare.quote, fare.mark);
          const end = watchEnd(fare.watch);
          return (
            <article
              key={`${fare.watch.watch_id}:${fare.mark.axis}`}
              className="min-w-0 border-t border-neutral-700/80 py-4 first:border-t-0 md:border-t-0 md:border-l md:px-4 md:[&:nth-child(3n+1)]:border-l-0 md:[&:nth-child(3n+1)]:pl-0 md:[&:nth-child(n+4)]:border-t"
            >
              <div className="flex items-baseline gap-2 text-xs text-neutral-400">
                <strong className={fare.watch.kind === "hotel" ? "text-neutral-100" : "font-mono text-neutral-100"}>
                  {routeLabel(fare.watch)}
                </strong>
                {end && (
                  <span className="font-mono text-[11px]">{shortDate(end)}</span>
                )}
              </div>
              <div className="mt-3 flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-mono text-xl font-semibold tabular-nums">
                    {money(fare.quote.axis, fare.quote.current)}
                  </div>
                  {result && <VerdictText verdict={result} className="mt-0.5 block text-xs" />}
                </div>
                <FareSparkline values={fare.quote.spark} atLow={fare.quote.atLow} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  disabled={busy(fare)}
                  onClick={() => onTrack(fare)}
                >
                  Track {money(fare.mark.axis, fare.quote.current)}…
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={busy(fare)}
                  onClick={() => onKeep(fare)}
                >
                  Keep {money(fare.mark.axis, fare.mark.amount)}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Confirms the new baseline before it is written: what a rebooking actually
 * costs can differ from the fare, so the fare is only the starting value.
 * Confirming also marks this reading reviewed, so the drop clears even when
 * the amount entered sits above it.
 */
function TrackSheet({
  fare,
  onClose,
  onDone,
}: {
  fare: PriceDrop;
  onClose: () => void;
  onDone: () => void;
}) {
  const axis = fare.mark.axis;
  const start = Object.values(trackCurrent(fare))[0] as number;
  const [text, setText] = useState(String(start));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const amount = parseAmount(axis, text);
  const paid = fare.mark.label === "paid";
  const unit = axis === "cash" ? "($)" : "(points)";
  const title = `Track ${routeLabel(fare.watch)}`;

  return (
    <Sheet title={title} onClose={onClose}>
      <form
        aria-label={title}
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (busy || amount === null) return;
          setBusy(true);
          setProblem(null);
          updateWatch(fare.watch.watch_id, trackAt(fare, amount))
            // The amount is what matters; a failed review only leaves the card up.
            .then(() => markReviewed(fare).catch(() => undefined))
            .then(onDone)
            .catch((err: unknown) => setProblem(message(err)))
            .finally(() => setBusy(false));
        }}
      >
        <dl className="divide-y divide-neutral-800 text-sm">
          <div className="flex justify-between gap-4 py-1.5">
            <dt className="text-neutral-500">Latest price</dt>
            <dd className="font-mono tabular-nums">{money(axis, fare.quote.current)}</dd>
          </div>
          <div className="flex justify-between gap-4 py-1.5">
            <dt className="text-neutral-500">{paid ? "Paid now" : "Target now"}</dt>
            <dd className="font-mono tabular-nums">{money(axis, fare.mark.amount)}</dd>
          </div>
        </dl>
        <label className="flex flex-col gap-1 text-xs text-neutral-400">
          {paid ? `New paid amount ${unit}` : `New target ${unit}`}
          <input
            type="text"
            inputMode="decimal"
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            className={INPUT}
          />
          <span className="text-[11px] text-neutral-500">
            {axis === "cash"
              ? "What you paid. Drops are measured from this."
              : "Drops are measured from this."}
          </span>
        </label>
        {problem && (
          <p role="alert" className="rounded-lg border border-red-900 bg-red-950/40 p-3 text-xs text-red-300">
            {problem}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
          <button type="submit" disabled={busy || amount === null} className="btn btn-primary">
            {busy ? "Saving…" : amount === null ? "Track" : `Track ${money(axis, amount)}`}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

const VERDICT_TONE: Record<Verdict["tone"], string> = {
  good: "text-green-400",
  bad: "text-red-400",
  mute: "text-neutral-400",
  floor: "text-amber-400",
};

function VerdictText({ verdict: v, className = "" }: { verdict: Verdict; className?: string }) {
  return (
    <span role="img" aria-label={v.label} title={v.label} className={`${VERDICT_TONE[v.tone]} ${className}`}>
      {v.text}
    </span>
  );
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const CHIP = "rounded-full border px-2 leading-5 text-xs";

/**
 * The morning's decisions, each with its button: a TripIt leg that looks like
 * something you track, a flight TripIt stopped listing, and booked flights
 * that came in without a fare.
 */
function NeedsYou({
  data,
  needs,
  isBusy,
  act,
  onAddPaid,
}: {
  data: FlightsDto;
  needs: ReturnType<typeof needsYou>;
  isBusy: (key: string) => boolean;
  act: (key: string, work: Promise<unknown>) => void;
  onAddPaid: () => void;
}) {
  const count = needs.matches.length + needs.missing.length + (needs.unpaid.length ? 1 : 0);
  if (count === 0) return null;
  const tripName = (w: Watch | null) =>
    data.trips.find((t) => t.id === w?.trip_id)?.name ?? "this trip";

  return (
    <section aria-label="Needs you" className="rounded-2xl border border-neutral-800 bg-neutral-900/60 p-4">
      <SectionTitle>Needs you · {count}</SectionTitle>
      <ul className="mt-2">
        {needs.matches.map(({ match, watch }) => {
          const p = match.proposed;
          const key = `m:${match.id}`;
          return (
            <Need key={key} dot="bg-accent"
              text={
                <>
                  TripIt has <b>{[p.flight, `${p.origin} → ${p.destination}`].filter(Boolean).join(" ")}</b>{" "}
                  on {shortDate(p.date)}. Is that the flight in {tripName(watch)}?
                </>
              }
              why={watch ? `It looks like ${routeLabel(watch)}, ${whenLabel(watch)}.` : ""}
            >
              {watch?.kind === "flight" && (
                <button type="button" disabled={isBusy(key)} className="btn btn-primary btn-sm"
                  onClick={() => act(key, resolveMatch(match.id, "merge"))}>
                  Same flight
                </button>
              )}
              <button type="button" disabled={isBusy(key)} className="btn btn-ghost btn-sm"
                onClick={() => act(key, resolveMatch(match.id, "separate"))}>
                Keep separate
              </button>
              <button type="button" disabled={isBusy(key)} className="btn btn-ghost btn-sm"
                onClick={() => act(key, dismissMatch(match.id))}>
                Dismiss
              </button>
            </Need>
          );
        })}
        {needs.missing.map((w) => {
          const key = `w:${w.watch_id}`;
          return (
            <Need key={key} dot="bg-red-400"
              text={<>{routeLabel(w)}, {whenLabel(w)}, left your TripIt feed on {shortDate(w.missing_since)}.</>}
              why="Wallet keeps tracking it until you say what happened."
            >
              <button type="button" disabled={isBusy(key)} className="btn btn-primary btn-sm"
                onClick={() => act(key, archiveWatch(w.watch_id))}>
                It's cancelled
              </button>
              <button type="button" disabled={isBusy(key)} className="btn btn-ghost btn-sm"
                onClick={() => act(key, updateWatch(w.watch_id, { missing_since: null }))}>
                Keep tracking
              </button>
            </Need>
          );
        })}
        {needs.unpaid.length > 0 && (
          <Need dot="bg-amber-400"
            text={`${needs.unpaid.length} booked flight${needs.unpaid.length === 1 ? " has" : "s have"} no fare.`}
            why="TripIt's feed carries no fares. Add what you paid so a drop can be measured."
          >
            <button type="button" className="btn btn-primary btn-sm" onClick={onAddPaid}>
              Add what I paid
            </button>
          </Need>
        )}
      </ul>
    </section>
  );
}

function Need({
  dot,
  text,
  why,
  children,
}: {
  dot: string;
  text: ReactNode;
  why: string;
  children: ReactNode;
}) {
  return (
    <li className="flex flex-wrap items-start gap-x-3 gap-y-2 border-b border-neutral-800/70 py-3 text-sm last:border-0">
      <span aria-hidden className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dot}`} />
      <div className="min-w-0 grow basis-60">
        <div>{text}</div>
        {why && <div className="mt-0.5 text-xs text-neutral-500">{why}</div>}
      </div>
      <div className="ml-5 flex shrink-0 gap-2 sm:ml-auto">{children}</div>
    </li>
  );
}

function TripSection({
  group,
  totals,
  onEditTrip,
  busy,
  isBusy,
  onEdit,
  prices,
  options,
  newest,
}: {
  group: Group;
  totals: ReturnType<typeof paidTotals>;
  onEditTrip: () => void;
  busy: boolean;
  isBusy: (key: string) => boolean;
  onEdit: (w: Watch) => void;
  prices: Record<string, PriceSeries[]>;
  options: Record<string, WatchOptions>;
  newest: number | null;
}) {
  const { trip, watches } = group;
  const span = group.start
    ? group.end && group.end !== group.start
      ? `${shortDate(group.start)} – ${shortDate(group.end)}`
      : shortDate(group.start)
    : "";

  return (
    <section aria-label={trip.name}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 className="text-lg leading-tight font-extrabold [font-stretch:112%]">
          {trip.name}
        </h2>
        {span && <span className="font-mono text-xs text-neutral-500">{span}</span>}
        {trip.traveler?.trim() && (
          <span className={`${CHIP} border-accent/60 text-accent`}>For {trip.traveler.trim()}</span>
        )}
        {trip.source === "tripit" && (
          <span className={`${CHIP} border-neutral-700 text-neutral-400`}>TripIt trip</span>
        )}
        {trip.archived && (
          <span className={`${CHIP} border-neutral-700 text-neutral-400`}>Archived trip</span>
        )}
        {totals && <TripPaidTotal totals={totals} />}
        <span className="ml-auto flex shrink-0 gap-2">
          <button type="button" disabled={busy} onClick={onEditTrip} className="btn btn-ghost btn-sm">
            Edit trip
          </button>
        </span>
      </div>
      {watches.length === 0 ? (
        <p className="mt-2 text-sm text-neutral-500">Nothing in this trip yet.</p>
      ) : (
        <ul className="mt-2 border-t border-neutral-800/70">
          {watches.map((w) => (
            <WatchRow
              key={w.watch_id}
              watch={w}
              prices={prices[w.watch_id]}
              options={options[w.watch_id]}
              newest={newest}
              busy={isBusy(`w:${w.watch_id}`)}
              onEdit={() => onEdit(w)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function TripPaidTotal({ totals }: { totals: NonNullable<ReturnType<typeof paidTotals>> }) {
  const amounts = [
    totals.hasCash ? money("cash", totals.cash) : null,
    totals.hasPoints ? money("award", totals.points) : null,
  ].filter(Boolean).join(" + ");
  const complete = totals.recorded === totals.bookings;
  const coverage = totals.bookings === 1
    ? "Paid"
    : complete
      ? `Paid · ${totals.bookings} bookings`
      : `Paid · ${totals.recorded}/${totals.bookings} bookings`;
  return (
    <span className="text-xs text-neutral-400">
      {coverage}
      <strong className="ml-1.5 font-mono text-neutral-200">{amounts}</strong>
    </span>
  );
}

function WatchRow({
  watch: w,
  prices,
  options,
  newest,
  busy,
  onEdit,
}: {
  watch: Watch;
  prices?: PriceSeries[];
  options?: WatchOptions;
  newest: number | null;
  busy: boolean;
  onEdit: () => void;
}) {
  const priced = shownQuotes(w, prices);
  const tracking = trackingParts(w);
  const notWatching = isNotWatching(w);
  const primaryBadge = badge(w);
  const chips: [string, string][] = primaryBadge ? [[
    primaryBadge,
    w.fields.booked ? "border-accent/40 text-accent" : "border-neutral-700 text-neutral-300",
  ]] : [];
  if (w.source === "tripit") chips.push(["From TripIt", "border-neutral-700 text-neutral-400"]);
  if (w.missing_since) chips.push(["Not in TripIt", "border-red-900 text-red-400"]);
  if (w.kind === "query" && !w.fields.active) chips.push(["Paused", "border-amber-900/70 text-amber-400"]);
  if (lockedIn(w, localDay())) chips.push(["Locked in", "border-neutral-700 text-neutral-400"]);

  return (
    <li className="border-b border-neutral-800/70 py-3 text-sm">
      <div className="grid items-center gap-x-3 gap-y-2 md:grid-cols-[minmax(15rem,1fr)_10rem_13rem_4.5rem]">
        <span className="flex min-w-0 items-start gap-2">
          <Icon
            name={w.kind === "hotel" ? "hotel" : "flights"}
            size={17}
            className="mt-0.5 shrink-0 text-neutral-500"
          />
          <span className="sr-only">{w.kind === "hotel" ? "Hotel" : "Flight"}</span>
          <span className="min-w-0">
            <strong className={w.kind === "hotel" ? "" : "font-mono tracking-tight"}>
              {routeLabel(w)}
            </strong>
            <span className="ml-2 font-mono text-xs text-neutral-400">{whenLabel(w)}</span>
          </span>
        </span>
        <span data-slot="status" className="flex flex-wrap gap-1.5 sm:self-center">
          {chips.map(([label, cls]) => (
            <span key={label} className={`${CHIP} ${cls}`}>
              {label}
            </span>
          ))}
        </span>
        <span data-slot="tracking" className="min-w-0">
          {notWatching && (
            <span className={`${CHIP} border-amber-900/70 text-amber-400`}>Not watching</span>
          )}
          {tracking.length > 0 && (
            <span className={`block ${notWatching ? "mt-1" : ""}`}>
              <span className="block text-[10px] font-semibold tracking-[0.08em] text-neutral-500 uppercase">
                {tracking.every((part) => part.label === "paid") ? "Measured against" : "Actively tracking"}
              </span>
              <span className="text-xs text-neutral-300">
                {tracking.map((part) => (
                  <span key={part.axis} className="mr-2 last:mr-0">
                    {part.label === "paid" ? "Paid" : part.axis === "cash" ? "Cash target" : "Points target"}{" "}
                    <b className="font-mono text-accent">{money(part.axis, part.amount)}</b>
                  </span>
                ))}
              </span>
            </span>
          )}
          {!notWatching && tracking.length === 0 && (
            <span aria-hidden className="hidden md:block">&nbsp;</span>
          )}
        </span>
        <span className="flex shrink-0 justify-end gap-2">
          <button type="button" disabled={busy} onClick={onEdit} className="btn btn-ghost btn-sm">
            Edit
          </button>
        </span>
      </div>
      {priced.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {priced.map((q) => (
            <QuoteRow key={q.axis} quote={q} watch={w} stale={isStale(q, newest)} />
          ))}
        </div>
      )}
      {neverPriced(w, prices) && (
        <div className="mt-2 rounded-lg bg-neutral-900/60 px-3 py-2 text-xs text-amber-400">
          No price yet · site-checker has never found one for this watch
        </div>
      )}
      {options?.award && options.award.pairs.length > 0 && <OptionsList options={options.award} />}
      {options?.cash && options.cash.pairs.length > 0 && <CashOptionsList options={options.cash} />}
    </li>
  );
}

const OPTIONS_SHOWN = 3;

/** A flexible search's cheapest round trips from site-checker's last run. */
function OptionsList({ options }: { options: Options }) {
  const shown = options.pairs.slice(0, OPTIONS_SHOWN);
  const more = options.pairs.length - shown.length;
  return (
    <div className="basis-full">
      <div className="mb-1 text-xs text-neutral-500">
        Best round trips, checked {readingDay(options.as_of)}
      </div>
      <ul className="space-y-1" aria-label="Best round trips">
        {shown.map((p) => (
          <li
            key={`${p.out.date}-${p.out.flight_number}-${p.ret.date}-${p.ret.flight_number}`}
            className="flex flex-wrap items-baseline gap-x-3 rounded-lg bg-neutral-900/60 px-3 py-1.5 text-xs"
          >
            <span className="font-mono">
              {shortDate(p.out.date)} → {shortDate(p.ret.date)}
            </span>
            <span className="text-neutral-400">{p.nights} nights</span>
            <span className="font-mono text-neutral-400">
              {[p.out.flight_number, p.ret.flight_number].filter(Boolean).join(" / ")}
            </span>
            <span className="ml-auto font-mono font-semibold tabular-nums">
              {money("award", p.total_miles)}
              {p.total_taxes > 0 && (
                <span className="font-normal text-neutral-400"> + {money("cash", p.total_taxes)}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
      {more > 0 && <div className="mt-1 text-xs text-neutral-500">+{more} more</div>}
    </div>
  );
}

/** A round trip's cheapest fare to each destination from site-checker's last run. */
function CashOptionsList({ options }: { options: CashOptions }) {
  return (
    <div className="basis-full">
      <div className="mb-1 text-xs text-neutral-500">
        Cheapest fares, checked {readingDay(options.as_of)}
      </div>
      <ul className="space-y-1" aria-label="Cheapest fares">
        {options.pairs.map((c) => {
          const stops = c.out.stops + c.ret.stops;
          return (
            <li
              key={c.dest}
              className="flex flex-wrap items-baseline gap-x-3 rounded-lg bg-neutral-900/60 px-3 py-1.5 text-xs"
            >
              <span className="font-mono">to {c.dest}</span>
              <span className="font-mono text-neutral-400">{[c.out.flight, c.ret.flight].join(" / ")}</span>
              {stops > 0 && (
                <span className="text-neutral-500">
                  {stops} stop{stops === 1 ? "" : "s"}
                </span>
              )}
              <span className="ml-auto font-mono font-semibold tabular-nums">{money("cash", c.price)}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The day of a site-checker timestamp. It stamps Pacific-offset ISO, so the
 * first ten characters are the Pacific day, never a UTC one. */
const readingDay = (iso: string) => shortDate(iso.slice(0, 10));

const programLabel = (p: string | null) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : "Award");

/** One price line on a card: the mockup's quote row. */
function QuoteRow({ quote: q, watch, stale }: { quote: Quote; watch: Watch; stale: boolean }) {
  const [history, setHistory] = useState(false);
  const v = verdict(q, markFor(watch, q.axis));
  const ident = q.axis === "award" ? programLabel(q.program) : watch.kind === "hotel" ? "Rate" : "Cash";
  const detail = [q.flight, watch.kind === "query" && q.dest ? `to ${q.dest}` : null]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-neutral-900/60 px-3 py-2">
      <div className="min-w-0 grow basis-48">
        <div className="text-[13px] font-semibold">
          {ident}
          {detail && <span className="ml-1.5 font-mono text-xs font-normal text-neutral-400">{detail}</span>}
          {q.others > 0 && (
            <span className="ml-1.5 text-xs font-normal text-neutral-500">+{q.others} more</span>
          )}
        </div>
        <div className="text-xs text-neutral-400">
          {q.count > 1
            ? `Range ${money(q.axis, q.low)}–${money(q.axis, q.high)} · ${q.count} prices · since ${readingDay(q.since)}`
            : `First seen ${readingDay(q.since)}`}
          {q.atLow && <span className="text-green-400"> · low</span>}
          {stale && <span className="text-amber-400"> · stale since {readingDay(q.checked)}</span>}
        </div>
      </div>
      <FareSparkline values={q.spark} atLow={q.atLow} />
      <div className="ml-auto text-right">
        <div className="font-mono text-base font-semibold tabular-nums">{money(q.axis, q.current)}</div>
        <div className="flex justify-end gap-2 text-xs">
          {v && <VerdictText verdict={v} />}
        </div>
      </div>
      <div className="basis-full text-right">
        <button
          type="button"
          aria-expanded={history}
          onClick={() => setHistory((open) => !open)}
          className="text-xs font-semibold text-neutral-400 hover:text-neutral-100"
        >
          {history ? "Hide history" : "History"}
        </button>
      </div>
      {history && (
        <div className="basis-full">
          <FareHistory axis={q.axis} points={q.history} />
        </div>
      )}
    </div>
  );
}

const JOBS: {
  key: FlightJob;
  label: string;
  note: string;
  question: string;
}[] = [
  {
    key: "tripit",
    label: "Sync TripIt",
    note: "Bookings and matches.",
    question: "Start the TripIt import now? It may update trips and watches.",
  },
  {
    key: "full",
    label: "Full refresh",
    note: "All prices, then wallet.",
    question: "Start a full price refresh? This uses paid SerpAPI calls, then copies the results into wallet.",
  },
  {
    key: "tripwire",
    label: "Tripwire",
    note: "Quick check, then wallet.",
    question: "Start a tripwire refresh? It may use a paid call, then copies the results into wallet.",
  },
];

/** Past its dates: the import archives these itself, so there is nothing to restore. */
function ended(w: Watch, now: Date, today: string): boolean {
  if (w.kind === "flight") return flown(w, now);
  const end = watchEnd(w);
  return end !== null && end < today;
}

/** Booked flights and hotels you archived whose dates are still ahead, each
 * restorable. Archived fare searches stay out: they are alternatives, not bookings. */
function ArchivedWatches({
  watches,
  trips,
  isBusy,
  onRestore,
}: {
  watches: Watch[];
  trips: Trip[];
  isBusy: (key: string) => boolean;
  onRestore: (w: Watch) => void;
}) {
  if (watches.length === 0) return null;
  const tripName = (w: Watch) => trips.find((t) => t.id === w.trip_id)?.name ?? "";
  return (
    <details aria-label="Archived watches" className="border-y border-neutral-800/70 py-3 text-sm">
      <summary className="cursor-pointer font-semibold text-neutral-400 hover:text-neutral-100">
        Archived · {watches.length}
      </summary>
      <ul className="mt-2">
        {watches.map((w) => (
          <li key={w.watch_id}
            className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-neutral-800/70 py-2 last:border-0">
            <span className="min-w-0 grow">
              <strong className={w.kind === "hotel" ? "" : "font-mono tracking-tight"}>
                {routeLabel(w)}
              </strong>
              <span className="ml-2 font-mono text-xs text-neutral-400">{whenLabel(w)}</span>
              {tripName(w) && <span className="ml-2 text-xs text-neutral-500">{tripName(w)}</span>}
            </span>
            <button type="button" disabled={isBusy(`w:${w.watch_id}`)}
              onClick={() => onRestore(w)} className="btn btn-ghost btn-sm">
              Restore
            </button>
          </li>
        ))}
      </ul>
    </details>
  );
}

function TrackerTools({
  estimate,
  notice,
  busy,
  onRun,
}: {
  estimate: ReturnType<typeof serpEstimate>;
  notice: string | null;
  busy: (job: FlightJob) => boolean;
  onRun: (job: FlightJob, question: string) => void;
}) {
  return (
    <details className="border-y border-neutral-800/70 py-3 text-sm">
      <summary className="cursor-pointer font-semibold text-neutral-400 hover:text-neutral-100">
        Tracker tools
      </summary>
      <div className="mt-4 grid gap-4 md:grid-cols-[minmax(13rem,0.7fr)_minmax(0,1.3fr)]">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.08em] text-neutral-500 uppercase">
            SerpAPI estimate
          </div>
          <div className="mt-1 font-mono text-xl font-semibold tabular-nums">
            ~{estimate.monthly.toLocaleString("en-US")} / month
          </div>
          <p className="mt-1 text-xs text-neutral-500">
            {estimate.daily} cash {estimate.daily === 1 ? "watch" : "watches"} × 365 ÷ 12. Excludes tripwire, flex, and dates already passed.
          </p>
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {JOBS.map((job) => (
            <div key={job.key} className="border-l border-neutral-800 pl-3">
              <button
                id={`run-${job.key}-job`}
                type="button"
                disabled={busy(job.key)}
                onClick={() => onRun(job.key, job.question)}
                className="btn btn-ghost btn-sm"
              >
                {busy(job.key) ? "Starting…" : job.label}
              </button>
              <p className="mt-1 text-xs text-neutral-500">{job.note}</p>
            </div>
          ))}
        </div>
      </div>
      {notice && <p role="status" className="mt-3 text-xs text-neutral-300">{notice}</p>}
    </details>
  );
}

function TripEditSheet({
  trip,
  canMerge,
  onMerge,
  onClose,
  onDone,
}: {
  trip: Trip;
  canMerge: boolean;
  onMerge: () => void;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(trip.name);
  const [traveler, setTraveler] = useState(trip.traveler ?? "");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const nameChanged = Boolean(name.trim()) && name.trim() !== trip.name;
  const travelerChanged = traveler.trim() !== (trip.traveler ?? "").trim();

  const perform = (work: Promise<unknown>) => {
    setBusy(true);
    setProblem(null);
    work
      .then(onDone)
      .catch((error: unknown) => setProblem(message(error)))
      .finally(() => setBusy(false));
  };

  return (
    <Sheet title={`Edit ${trip.name}`} onClose={onClose}>
      <form
        aria-label={`Edit ${trip.name}`}
        className="space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy || (!nameChanged && !travelerChanged)) return;
          perform(updateTrip(trip.id, {
            ...(nameChanged ? { name: name.trim() } : {}),
            // Sent even when blank: that is how the tag is cleared, which a
            // field left out of the patch could not express.
            ...(travelerChanged ? { traveler: traveler.trim() } : {}),
          }));
        }}
      >
        <label className="flex flex-col gap-1 text-xs text-neutral-400">
          Trip name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            className={INPUT}
          />
        </label>
        <div className="flex flex-col gap-1">
          <label className="flex flex-col gap-1 text-xs text-neutral-400">
            Whose trip
            <input
              value={traveler}
              onChange={(event) => setTraveler(event.target.value)}
              placeholder="Yours. Name someone to tag it for them."
              className={INPUT}
            />
          </label>
          <span className="text-[11px] text-neutral-500">
            Filters the list by this name. Capitals and extra spaces do not start a second person.
          </span>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
          <button
            type="submit"
            disabled={busy || (!nameChanged && !travelerChanged)}
            className="btn btn-primary"
          >
            {busy ? "Saving…" : "Save trip"}
          </button>
        </div>

        {!trip.archived && (
          <div className="space-y-2 border-t border-neutral-800 pt-4">
            <p className="text-xs leading-relaxed text-neutral-500">
              These are occasional trip-level actions. Merging preserves every watch and its price history.
            </p>
            <div className="flex flex-wrap gap-2">
              <button type="button" disabled={!canMerge || busy} onClick={onMerge} className="btn btn-ghost">
                Merge into another trip
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => perform(archiveTrip(trip.id))}
                className="btn btn-ghost text-red-300 hover:text-red-200"
              >
                Archive trip
              </button>
            </div>
            {!canMerge && <p className="text-xs text-neutral-500">Add another trip before merging this one.</p>}
          </div>
        )}
        {problem && <p role="alert" className="text-sm text-red-400">{problem}</p>}
      </form>
    </Sheet>
  );
}

function MergeTripSheet({
  source,
  trips,
  watches,
  onClose,
  onDone,
}: {
  source: Trip;
  trips: Trip[];
  watches: Watch[];
  onClose: () => void;
  onDone: () => void;
}) {
  const targets = trips.filter((trip) =>
    trip.id !== source.id && !trip.archived && !trip.merged_into_id,
  );
  const [target, setTarget] = useState(targets[0]?.id ?? 0);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const moving = watches.filter((watch) => watch.trip_id === source.id);
  const destination = targets.find((trip) => trip.id === target) ?? null;

  return (
    <Sheet title={`Merge ${source.name}`} onClose={onClose}>
      <form
        aria-label={`Merge ${source.name}`}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!destination || busy) return;
          setBusy(true);
          setProblem(null);
          mergeTrip(source.id, destination.id)
            .then(onDone)
            .catch((error: unknown) => setProblem(message(error)))
            .finally(() => setBusy(false));
        }}
      >
        <p className="text-sm leading-relaxed text-neutral-400">
          Choose the trip that remains. Every watch moves there, including archived ones.
          Price history stays attached to each watch.
        </p>
        {targets.length > 0 ? (
          <fieldset className="space-y-2">
            <legend className="mb-1 text-xs font-semibold tracking-[0.08em] text-neutral-500 uppercase">
              Keep this trip
            </legend>
            {targets.map((trip) => {
              const count = watches.filter((watch) => watch.trip_id === trip.id).length;
              return (
                <label
                  key={trip.id}
                  className={`block cursor-pointer rounded-xl border p-3 text-sm ${
                    target === trip.id ? "border-accent bg-accent/5" : "border-neutral-700"
                  }`}
                >
                  <input
                    type="radio"
                    name="target-trip"
                    value={trip.id}
                    checked={target === trip.id}
                    onChange={() => setTarget(trip.id)}
                    className="mr-2 accent-sky-400"
                  />
                  <strong>{trip.name}</strong>
                  <span className="ml-2 text-xs text-neutral-500">
                    {count} {count === 1 ? "watch" : "watches"}
                  </span>
                </label>
              );
            })}
          </fieldset>
        ) : (
          <p className="rounded-xl border border-amber-900/70 bg-amber-950/30 p-3 text-sm text-amber-300">
            Add another trip before merging this one.
          </p>
        )}
        {destination && (
          <dl className="border-y border-neutral-700 py-2 text-sm">
            <div className="flex justify-between gap-4 py-1.5">
              <dt className="text-neutral-500">Watches moving</dt><dd>{moving.length}</dd>
            </div>
            <div className="flex justify-between gap-4 py-1.5">
              <dt className="text-neutral-500">Trip that remains</dt><dd>{destination.name}</dd>
            </div>
            <div className="flex justify-between gap-4 py-1.5">
              <dt className="text-neutral-500">Price history</dt><dd>Preserved</dd>
            </div>
            <div className="flex justify-between gap-4 py-1.5">
              <dt className="text-neutral-500">{source.name}</dt><dd>Archived</dd>
            </div>
          </dl>
        )}
        {problem && <p role="alert" className="text-sm text-red-400">{problem}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn btn-ghost">Cancel</button>
          <button type="submit" disabled={!destination || busy} className="btn btn-primary">
            {busy ? "Merging…" : `Merge ${moving.length} ${moving.length === 1 ? "watch" : "watches"}`}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

/** Add (a picker, then the form) or edit (straight to the watch's form). */
function WatchSheet({
  data,
  watch,
  onClose,
  onDone,
}: {
  data: FlightsDto;
  watch?: Watch;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState<Form | null>(watch ? formOf(watch) : null);
  const title = watch ? `Edit ${routeLabel(watch)}` : form ? FORMS[form].title : "Track a flight";

  return (
    <Sheet title={title} onClose={onClose}>
      {form ? (
        <WatchForm
          data={data}
          form={form}
          watch={watch}
          onBack={watch ? undefined : () => setForm(null)}
          onArchive={watch ? () => archiveWatch(watch.watch_id).then(onDone) : undefined}
          onWatching={watch
            ? () => updateWatch(watch.watch_id, { watching: !isWatched(watch) }).then(onDone)
            : undefined}
          onDone={onDone}
        />
      ) : (
        <div className="grid gap-2" role="group" aria-label="What are you tracking?">
          {(Object.keys(FORMS) as Form[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setForm(f)}
              className="rounded-xl border border-neutral-800 bg-neutral-950 p-3 text-left hover:border-neutral-600"
            >
              <span className="block text-sm font-semibold">{FORMS[f].title}</span>
              <span className="mt-0.5 block text-xs text-neutral-400">{FORMS[f].blurb}</span>
            </button>
          ))}
        </div>
      )}
    </Sheet>
  );
}

const INPUT =
  "min-h-10 rounded-lg border border-neutral-700 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 disabled:opacity-40";

function WatchForm({
  data,
  form,
  watch,
  onBack,
  onArchive,
  onWatching,
  onDone,
}: {
  data: FlightsDto;
  form: Form;
  watch?: Watch;
  onBack?: () => void;
  onArchive?: () => Promise<unknown>;
  onWatching?: () => Promise<unknown>;
  onDone: () => void;
}) {
  const [values, setValues] = useState<FormValues>(() => formValues(form, watch));
  const [trip, setTrip] = useState<string>(
    watch
      ? String(watch.trip_id)
      : String(data.trips.find((t) => !t.archived)?.id ?? "new"),
  );
  const [newTrip, setNewTrip] = useState("");
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const fields = fieldsFromForm(form, values);
  const duplicate = watch ? null : findDuplicate(data, form, fields);
  const nameTaken = duplicate !== null && FORMS[form].kind === "query";
  const set = (name: string, v: string | boolean) => setValues((old) => ({ ...old, [name]: v }));
  const specs = FIELDS[form];
  const perform = (work: () => Promise<unknown>) => {
    setBusy(true);
    setProblem(null);
    work()
      .catch((err: unknown) => setProblem(message(err)))
      .finally(() => setBusy(false));
  };

  const submit = async () => {
    const missing = missingRequired(form, fields);
    if (missing) throw new Error(`${missing} is required.`);
    if (trip === "new" && !newTrip.trim()) throw new Error("Name the new trip, or pick one.");
    const tripId = trip === "new" ? (await createTrip(newTrip.trim())).id : Number(trip);
    if (!watch) {
      await createWatch(FORMS[form].kind, tripId, withoutBlanks(fields));
      return;
    }
    // Against the form's own starting point, not the stored row: an untouched
    // checkbox must not turn a blank into an explicit "no".
    const changes = changedFields(fieldsFromForm(form, formValues(form, watch)), fields);
    const moved = tripId !== watch.trip_id;
    if (Object.keys(changes).length || moved) {
      await updateWatch(watch.watch_id, moved ? { ...changes, trip_id: tripId } : changes);
    }
  };

  return (
    <form
      aria-label={FORMS[form].title}
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy || nameTaken) return;
        setBusy(true);
        setProblem(null);
        submit()
          .then(onDone)
          .catch((err: unknown) => setProblem(message(err)))
          .finally(() => setBusy(false));
      }}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="flex flex-col gap-1 text-xs text-neutral-400">
          Trip
          <select value={trip} onChange={(e) => setTrip(e.target.value)} className={INPUT}>
            {data.trips
              .filter((t) => !t.archived || t.id === watch?.trip_id)
              .map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            <option value="new">New trip…</option>
          </select>
        </label>
        {trip === "new" && (
          <label className="flex flex-col gap-1 text-xs text-neutral-400">
            New trip name
            <input value={newTrip} onChange={(e) => setNewTrip(e.target.value)} className={INPUT} />
          </label>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {specs.filter((s) => !s.more).map((s) => (
          <FieldInput key={s.name} spec={s} values={values} set={set} />
        ))}
      </div>

      {specs.some((s) => s.more) && (
        <>
          <Fold open={more} onClick={() => setMore((m) => !m)} label="More filters" />
          {more && (
            <div className="grid gap-2 sm:grid-cols-2">
              {specs.filter((s) => s.more).map((s) => (
                <FieldInput key={s.name} spec={s} values={values} set={set} />
              ))}
            </div>
          )}
        </>
      )}

      {duplicate && (
        <p role="status" className="rounded-lg border border-amber-900/70 bg-amber-950/30 p-3 text-xs text-amber-300">
          {nameTaken
            ? `A trip search named "${String(fields.name)}" already exists. site-checker needs the names to be different.`
            : `Already tracked: ${routeLabel(duplicate)}, ${whenLabel(duplicate)}. Saving adds a second watch.`}
        </p>
      )}
      {problem && (
        <p role="alert" className="rounded-lg border border-red-900 bg-red-950/40 p-3 text-xs text-red-300">
          {problem}
        </p>
      )}

      {watch && onWatching && onArchive && (
        <p className="text-xs text-neutral-500">
          Stop watching keeps it on Travel without price checks. Archive takes it off
          Travel, and TripIt will not add it back.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {watch && onWatching && (
          <button type="button" disabled={busy} onClick={() => perform(onWatching)}
            className="btn btn-ghost">
            {isWatched(watch) ? "Stop watching" : "Resume watching"}
          </button>
        )}
        {onArchive && (
          <button
            type="button"
            disabled={busy}
            onClick={() => perform(onArchive)}
            className="btn btn-ghost text-red-300 hover:text-red-200"
          >
            Archive
          </button>
        )}
        {onBack && (
          <button type="button" onClick={onBack} className="btn btn-ghost">
            Back
          </button>
        )}
        <span className="grow" />
        <button type="submit" disabled={busy || nameTaken} className="btn btn-primary">
          {busy ? "Saving…" : watch ? "Save changes" : "Start tracking"}
        </button>
      </div>
    </form>
  );
}

const cabinLabel = (c: string) => c.charAt(0).toUpperCase() + c.slice(1).replace(/_/g, " ");

const choiceOptions = (
  choices: readonly (readonly [string, string])[],
  value: string,
) => choices.some(([key]) => key === value) || !value
  ? choices
  : [...choices, [value, `${value} (imported)`] as const];

function FieldInput({
  spec,
  values,
  set,
}: {
  spec: FieldSpec;
  values: FormValues;
  set: (name: string, v: string | boolean) => void;
}) {
  const value = values[spec.name];
  const text = typeof value === "string" ? value : "";
  const label = `${spec.label}${spec.required ? " *" : ""}`;

  if (spec.input === "check") {
    return (
      <label className="flex min-h-10 items-center gap-2 text-xs text-neutral-400 sm:col-span-2">
        <input
          type="checkbox"
          className="h-4 w-4 accent-sky-400"
          checked={Boolean(value)}
          onChange={(e) => set(spec.name, e.target.checked)}
        />
        {spec.label}
      </label>
    );
  }

  const skipped = spec.input === "amount" && Boolean(values[`${spec.name}:skip`]);
  let control;
  if (spec.input === "cabin") {
    // An imported cabin outside the list stays selectable, so an edit that
    // never touches it does not quietly change it.
    const options = CABINS.includes(text) || !text ? CABINS : [...CABINS, text];
    control = (
      <select value={text} onChange={(e) => set(spec.name, e.target.value)} className={INPUT}>
        {options.map((c) => (
          <option key={c} value={c}>
            {cabinLabel(c)}
          </option>
        ))}
      </select>
    );
  } else if (spec.input === "program") {
    const options = choiceOptions([["", "Any supported program"], ...FLIGHT_AWARD_PROGRAMS], text);
    control = (
      <select value={text} onChange={(e) => set(spec.name, e.target.value)} className={INPUT}>
        {options.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
      </select>
    );
  } else if (spec.input === "booked-via") {
    const options = choiceOptions(BOOKING_CHANNELS, text);
    control = (
      <select value={text} onChange={(e) => set(spec.name, e.target.value)} className={INPUT}>
        {options.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
      </select>
    );
  } else if (spec.input === "programs") {
    const selected = new Set(text.split(",").map((p) => p.trim()).filter(Boolean));
    const known = new Set<string>(AWARD_PROGRAMS.map(([code]) => code));
    const unknown = [...selected].filter((code) => !known.has(code));
    return (
      <div className="flex flex-col gap-1">
        <span className="text-xs text-neutral-400">{label}</span>
        <details className="relative">
          <summary className={`${INPUT} flex cursor-pointer list-none items-center justify-between`}>
            <span className="truncate">
              {selected.size ? `${selected.size} selected` : "Any supported program"}
            </span>
            <span aria-hidden className="text-neutral-500">⌄</span>
          </summary>
          <div className="mt-1 max-h-64 space-y-1 overflow-y-auto rounded-lg border border-neutral-700 bg-neutral-950 p-2 shadow-xl">
            {[...AWARD_PROGRAMS, ...unknown.map((code) => [code, `${code} (imported)`] as const)].map(([code, name]) => (
              <label key={code} className="flex items-center gap-2 rounded px-2 py-1 text-xs text-neutral-300 hover:bg-neutral-900">
                <input
                  type="checkbox"
                  checked={selected.has(code)}
                  onChange={(e) => {
                    const next = new Set(selected);
                    if (e.target.checked) next.add(code); else next.delete(code);
                    set(spec.name, [...next].join(", "));
                  }}
                  className="h-3.5 w-3.5 accent-sky-400"
                />
                <span>{name}</span>
              </label>
            ))}
          </div>
        </details>
        {spec.hint && <span className="text-[11px] text-neutral-500">{spec.hint}</span>}
      </div>
    );
  } else {
    const type =
      spec.input === "date" ? "date"
        : spec.input === "time" ? "time"
          : spec.input === "number" || spec.input === "amount" ? "number"
            : "text";
    control = (
      <input
        type={type}
        value={text}
        disabled={skipped}
        inputMode={type === "number" ? "decimal" : undefined}
        maxLength={spec.input === "airport" ? 3 : undefined}
        autoCapitalize={spec.input === "airport" || spec.input === "airports" ? "characters" : undefined}
        onChange={(e) => set(spec.name, e.target.value)}
        className={INPUT}
      />
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="flex flex-col gap-1 text-xs text-neutral-400">
        {label}
        {control}
      </label>
      {spec.hint && <span className="text-[11px] text-neutral-500">{spec.hint}</span>}
      {spec.skip && (
        <label className="flex items-center gap-2 text-[11px] text-neutral-500">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-sky-400"
            checked={skipped}
            onChange={(e) => set(`${spec.name}:skip`, e.target.checked)}
          />
          {spec.skip}
        </label>
      )}
    </div>
  );
}

/** What you paid, for every booked flight that came in without a fare. */
function PaidSheet({
  watches,
  onClose,
  onDone,
}: {
  watches: Watch[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [paid, setPaid] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  return (
    <Sheet title="What did you pay?" onClose={onClose}>
      <form
        aria-label="What did you pay?"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (busy) return;
          const entered = watches.filter((w) => (paid[w.watch_id] ?? "").trim() !== "");
          setBusy(true);
          setProblem(null);
          Promise.all(
            entered.map((w) =>
              updateWatch(w.watch_id, { price_tracked: Number(paid[w.watch_id].replace(/[$,]/g, "")) }),
            ),
          )
            .then(onDone)
            .catch((err: unknown) => setProblem(message(err)))
            .finally(() => setBusy(false));
        }}
      >
        <p className="text-sm text-neutral-400">
          TripIt's feed carries no fares. Add them and drops count from what you actually paid.
        </p>
        {watches.map((w) => (
          <label key={w.watch_id} className="flex flex-col gap-1 text-xs text-neutral-400">
            {routeLabel(w)} · {whenLabel(w)}
            <input
              type="number"
              inputMode="decimal"
              value={paid[w.watch_id] ?? ""}
              onChange={(e) => setPaid((p) => ({ ...p, [w.watch_id]: e.target.value }))}
              className={INPUT}
            />
          </label>
        ))}
        {problem && (
          <p role="alert" className="rounded-lg border border-red-900 bg-red-950/40 p-3 text-xs text-red-300">
            {problem}
          </p>
        )}
        <button type="submit" disabled={busy} className="btn btn-primary">
          {busy ? "Saving…" : "Save prices"}
        </button>
      </form>
    </Sheet>
  );
}
